import React from 'react';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

/**
 * SafeIcon — a drop-in replacement for the MaterialCommunityIcons icon that
 * guarantees a renderable glyph. When an icon name is missing from the font
 * (typos, renamed glyphs across versions, or `-outline` variants that don't
 * exist) the raw component renders a literal "?" placeholder. Here we validate
 * the requested name against the font's glyph map and fall back to a neutral,
 * always-present glyph instead (see QA MGMT-021).
 *
 * @param {{name?: string, fallback?: string}} props standard icon props.
 */
const FALLBACK_GLYPH = 'checkbox-blank-circle-outline';

/**
 * Resolve the icon set's glyph map across `react-native-vector-icons` versions.
 *
 * Pre-v10 exposed `Icon.glyphMap`; v10+ removed that static in favour of
 * `Icon.hasIcon(name)` / `Icon.getRawGlyphMap()`. Reading the now-missing
 * `.glyphMap` made EVERY name fail validation, so all icons collapsed to the
 * fallback circle (most visibly the management tab bar on the web build).
 * This helper probes the available API so validation works on either version.
 *
 * @param {string} name candidate icon name.
 * @returns {boolean} whether the font ships a glyph for `name`.
 */
const fontHasGlyph = (name) => {
    if (!name) return false;
    if (typeof MaterialCommunityIcons.hasIcon === 'function') {
        return MaterialCommunityIcons.hasIcon(name);
    }
    const glyphMap = typeof MaterialCommunityIcons.getRawGlyphMap === 'function'
        ? MaterialCommunityIcons.getRawGlyphMap()
        : MaterialCommunityIcons.glyphMap;
    return !!glyphMap && glyphMap[name] !== undefined;
};

/**
 * Glyphs the shipped font (react-native-vector-icons 10.2.0 → MDI 7.x subset) lacks
 * but that the app references, mapped to the closest glyph it does ship. Without
 * this the tile would fall back to a plain circle.
 * @type {Record<string, string>}
 */
export const ICON_ALIASES = {
    'receipt-text-outline': 'receipt',
    'receipt-text-plus-outline': 'receipt',
    'account-hard-hat-outline': 'account-hard-hat',
    'package-variant-closed-remove': 'package-variant-closed',
    'package-variant-closed-check': 'package-variant-closed',
    'bell-cog-outline': 'bell-outline',
    'flash-off-outline': 'flash-off',
    'shape-plus-outline': 'shape-plus',
};

export const resolveIconName = (name, fallback = FALLBACK_GLYPH) => {
    if (fontHasGlyph(name)) {
        return name;
    }
    if (ICON_ALIASES[name] && fontHasGlyph(ICON_ALIASES[name])) {
        return ICON_ALIASES[name];
    }
    // Ensure the fallback itself is valid; otherwise use a hard-coded glyph
    // that always ships with the font.
    if (fontHasGlyph(fallback)) {
        return fallback;
    }
    return FALLBACK_GLYPH;
};

const SafeIcon = ({name, fallback, ...rest}) => (
    <MaterialCommunityIcons name={resolveIconName(name, fallback)} {...rest} />
);

export default SafeIcon;
